import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    graphCountsForOperation,
    isWebsiteIngestionOperation,
    type KnowledgeGraphCounts,
} from '@/domain/knowledgeOperations';
import { queryKeys } from '@/lib/queryKeys';
import { listKnowledgeOperations } from '@/services/knowledgeOperations';
import {
    ACTIVE_WEBSITE_SYNC_RUN_STATUSES,
    type ApplyWebsiteDocumentPolicyChangesArgs,
    type ApplyWebsiteRescanReviewArgs,
    addWebsiteSource,
    applyWebsiteDocumentPolicyChanges,
    applyWebsiteRescanReview,
    type BulkUpdateDocumentDecisionArgs,
    type BulkUpdatePageDecisionArgs,
    bulkUpdateWebsiteDocumentDecision,
    bulkUpdateWebsitePageDecision,
    type DismissWebsiteRescanReviewArgs,
    dismissWebsiteRescanReview,
    fetchAllWebsiteRescanReviewPages,
    fetchAllWebsiteSyncPages,
    fetchCurrentWebsiteRescanReview,
    fetchNewlySelectedIncludedDocuments,
    fetchUnfinishedWebsiteDocuments,
    fetchWebsiteEverIngested,
    fetchWebsiteSources,
    fetchWebsiteSyncCapabilities,
    fetchWebsiteSyncRuns,
    type RefreshWebsiteDocumentsArgs,
    refreshWebsiteDocuments,
    retireWebsiteSource,
    type StartWebsiteRescanArgs,
    startWebsiteRescan,
    type UpdatePageDecisionArgs,
    updateWebsitePageDecision,
    type WebsiteSyncPagesFilters,
    type WebsiteSyncPagesResult,
} from '@/services/websiteSync';

type WebsiteSyncPagesSnapshot = Array<[QueryKey, WebsiteSyncPagesResult | undefined]>;

function getFiltersFromPagesQueryKey(queryKey: QueryKey): WebsiteSyncPagesFilters {
    const filters = queryKey[3];
    if (!filters || typeof filters !== 'object') return {};
    return filters as WebsiteSyncPagesFilters;
}

function getOptimisticPagesResult(
    current: WebsiteSyncPagesResult | undefined,
    args: BulkUpdateDocumentDecisionArgs,
    filters: WebsiteSyncPagesFilters
): WebsiteSyncPagesResult | undefined {
    if (!current) return current;

    const documentIds = new Set(args.documentIds);
    const decisionFilter = filters.decision;
    const shouldFilterByDecision = decisionFilter === 'include' || decisionFilter === 'exclude';
    const updatedItems = current.items.map((item) => {
        if (!documentIds.has(item.pageId)) return item;
        return {
            ...item,
            decision: args.decision,
            decisionReason: args.reason ?? null,
        };
    });

    if (!shouldFilterByDecision) {
        return {
            ...current,
            items: updatedItems,
        };
    }

    const beforeVisibleCount = current.items.filter(
        (item) => documentIds.has(item.pageId) && item.decision === decisionFilter
    ).length;
    const afterVisibleCount = updatedItems.filter(
        (item) => documentIds.has(item.pageId) && item.decision === decisionFilter
    ).length;
    const items = updatedItems.filter((item) => item.decision === decisionFilter);

    return {
        ...current,
        items,
        total: Math.max(items.length, current.total + afterVisibleCount - beforeVisibleCount),
    };
}

export function useWebsiteSyncRunsQuery(workspaceId: string) {
    return useQuery({
        queryKey: queryKeys.websiteSync.runs(workspaceId),
        queryFn: () => fetchWebsiteSyncRuns(workspaceId),
        enabled: !!workspaceId,
        refetchInterval: (query) => {
            const runs = query.state.data;
            if (runs?.some((run) => ACTIVE_WEBSITE_SYNC_RUN_STATUSES.has(run.status))) return 5000;
            return false;
        },
        staleTime: 10_000,
    });
}

export function useWebsiteSyncCapabilitiesQuery(enabled: boolean) {
    return useQuery({
        queryKey: queryKeys.websiteSync.capabilities(),
        queryFn: fetchWebsiteSyncCapabilities,
        enabled,
        staleTime: 30_000,
    });
}

export function useUnfinishedWebsiteDocumentsQuery(workspaceId: string, enabled: boolean) {
    return useQuery({
        queryKey: queryKeys.websiteSync.unfinishedDocuments(workspaceId),
        queryFn: () => fetchUnfinishedWebsiteDocuments(workspaceId),
        enabled: enabled && !!workspaceId,
        staleTime: 0,
    });
}

export function useRefreshWebsiteDocumentsMutation() {
    return useMutation({
        mutationFn: (args: RefreshWebsiteDocumentsArgs) => refreshWebsiteDocuments(args),
    });
}

export function useCurrentWebsiteRescanReviewQuery(workspaceId: string) {
    return useQuery({
        queryKey: queryKeys.websiteSync.currentRescanReview(workspaceId),
        queryFn: () => fetchCurrentWebsiteRescanReview(workspaceId),
        enabled: !!workspaceId,
        refetchInterval: (query) => {
            const review = query.state.data;
            if (
                review?.status === 'scanning' &&
                (review.scan_operation_status === 'queued' ||
                    review.scan_operation_status === 'running' ||
                    review.scan_operation_status === 'cancelling')
            ) {
                return 2_000;
            }
            if (
                review?.status === 'approved' &&
                (review.apply_operation_status === 'queued' ||
                    review.apply_operation_status === 'running' ||
                    review.apply_operation_status === 'cancelling')
            ) {
                return 2_000;
            }
            return false;
        },
        staleTime: 2_000,
    });
}

export function useWebsiteRescanReviewPagesQuery(
    workspaceId: string,
    reviewId: string | null,
    enabled: boolean
) {
    return useQuery({
        queryKey: queryKeys.websiteSync.rescanReviewPages(workspaceId, reviewId ?? 'none'),
        queryFn: () => fetchAllWebsiteRescanReviewPages(reviewId ?? '', workspaceId),
        enabled: enabled && !!workspaceId && !!reviewId,
        staleTime: 30_000,
    });
}

// Newest terminal ops page is checked first, so the first match walking the
// pages is already the most recent — bounded so a workspace with many retrains
// (document_loader ops can fill pages ahead of a website ingestion) doesn't
// spuriously report "never ingested".
const LAST_INGESTION_MAX_PAGES = 5;

// When did the live knowledge base last match the website selection? The newest
// *succeeded* scrape ingestion operation. Only succeeded counts: a failed,
// cancelled, or partial op has a completed_at but did not fully reach the KB,
// so it must not advance the watermark (that would hide un-ingested pages).
// Null when the corpus was never successfully ingested.
export function useWebsiteLastIngestionAtQuery(workspaceId: string) {
    return useQuery({
        queryKey: queryKeys.websiteSync.lastIngestion(workspaceId),
        queryFn: async (): Promise<string | null> => {
            let cursor: string | null | undefined;
            for (let page = 0; page < LAST_INGESTION_MAX_PAGES; page++) {
                const list = await listKnowledgeOperations(workspaceId, 'terminal', cursor);
                for (const operation of list.items) {
                    if (operation.status !== 'succeeded') continue;
                    if (!isWebsiteIngestionOperation(operation)) continue;
                    if (operation.completed_at) return operation.completed_at;
                }
                cursor = list.next_cursor;
                if (!cursor) break;
            }
            return null;
        },
        enabled: !!workspaceId,
        staleTime: 30_000,
    });
}

// Was this workspace's website content ever scraped into the corpus? Unlike the
// watermark above, this asks the corpus instead of the environment-scoped
// operation log, so it stays true for a site onboarded through another
// deployment (IX-4822). See `fetchWebsiteEverIngested` for what it does not
// prove — it is not evidence that the document-loader ran.
export function useWebsiteEverIngestedQuery(workspaceId: string, refetchWhileActive: boolean) {
    return useQuery({
        queryKey: queryKeys.websiteSync.everIngested(workspaceId),
        queryFn: () => fetchWebsiteEverIngested(workspaceId),
        enabled: !!workspaceId,
        // Keep an open playground in sync even when the crawl finishes through
        // a legacy worker or another deployment. Once ready, polling can stop.
        refetchInterval: (query) =>
            refetchWhileActive && query.state.data === false ? 5_000 : false,
        staleTime: 30_000,
    });
}

// Included pages *discovered* (documents.created_at) after the last successful
// ingestion — the common drift case (a rescan adds net-new pages). Disabled
// while a mapping/ingestion is in flight (the answer would be transient).
//
// Scope limit: keys on discovery time, so an OLD page the decider flips
// exclude->include on a later map is not caught (its created_at predates the
// watermark). Fixing that needs a decision-change timestamp — corpus_documents
// exposes none, and documents.updated_at is unusable (it bumps on every map for
// unchanged pages). Proper fix = expose latest include-decision time
// (knowledge.crawl_page_decisions) on the view. Tracked in IX-4122.
export function useNewlySelectedDocumentsQuery(
    workspaceId: string,
    sinceIso: string | null,
    enabled: boolean
) {
    return useQuery({
        queryKey: queryKeys.websiteSync.newlySelected(workspaceId, sinceIso),
        queryFn: () => fetchNewlySelectedIncludedDocuments(workspaceId, sinceIso),
        enabled: enabled && !!workspaceId,
        staleTime: 10_000,
    });
}

export function useWebsiteSyncPagesQuery(
    workspaceId: string,
    filters: WebsiteSyncPagesFilters,
    refetchWhileActive: boolean,
    enabled = true
) {
    return useQuery({
        queryKey: queryKeys.websiteSync.pages(workspaceId, filters),
        queryFn: () => fetchAllWebsiteSyncPages(workspaceId, filters),
        enabled: enabled && !!workspaceId,
        refetchInterval: refetchWhileActive ? 5000 : false,
        staleTime: 10_000,
    });
}

// Current knowledge-graph totals for the workspace. The graph only changes via
// ingestion, and each completed operation persists its post-ingest counts, so
// the newest terminal operation carrying counts reflects the live graph state.
export function useWebsiteGraphCountsQuery(workspaceId: string) {
    return useQuery({
        queryKey: queryKeys.websiteSync.graphCounts(workspaceId),
        queryFn: async (): Promise<KnowledgeGraphCounts | null> => {
            const list = await listKnowledgeOperations(workspaceId, 'terminal');
            for (const operation of list.items) {
                const counts = graphCountsForOperation(operation);
                if (counts) return counts;
            }
            return null;
        },
        enabled: !!workspaceId,
        staleTime: 30_000,
    });
}

export function useWebsiteSourcesQuery(workspaceId: string) {
    return useQuery({
        queryKey: queryKeys.websiteSync.sources(workspaceId),
        queryFn: () => fetchWebsiteSources(workspaceId),
        enabled: !!workspaceId,
        staleTime: 30_000,
    });
}

export function useAddWebsiteSourceMutation(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        onMutate: () => ({ workspaceId }),
        mutationFn: (rootUrl: string) => addWebsiteSource({ workspaceId, rootUrl }),
        onSuccess: (_data, _args, context) => {
            const workspaceId = context.workspaceId;
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.unfinishedDocuments(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.sources(workspaceId),
            });
        },
    });
}

export function useRetireWebsiteSourceMutation(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        onMutate: () => ({ workspaceId }),
        mutationFn: (websiteSourceId: string) =>
            retireWebsiteSource({ workspaceId, websiteSourceId }),
        onSuccess: (_data, _args, context) => {
            const workspaceId = context.workspaceId;
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.unfinishedDocuments(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.sources(workspaceId),
            });
        },
    });
}

export function useStartWebsiteRescanMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (args: StartWebsiteRescanArgs) => startWebsiteRescan(args),
        onSuccess: (_data, args) => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.runs(args.workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.pagesAll(args.workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.rescanReviewsAll(args.workspaceId),
            });
        },
    });
}

export function useApplyWebsiteRescanReviewMutation(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        onMutate: () => ({ workspaceId }),
        mutationFn: (args: ApplyWebsiteRescanReviewArgs) => applyWebsiteRescanReview(args),
        onSuccess: (_data, _args, context) => {
            const workspaceId = context.workspaceId;
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.rescanReviewsAll(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.runs(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.pagesAll(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.lastIngestion(workspaceId),
            });
        },
    });
}

export function useDismissWebsiteRescanReviewMutation(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        onMutate: () => ({ workspaceId }),
        mutationFn: (args: DismissWebsiteRescanReviewArgs) => dismissWebsiteRescanReview(args),
        onSettled: (_data, _error, _args, context) => {
            if (!context) return;
            const workspaceId = context.workspaceId;
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.rescanReviewsAll(workspaceId),
            });
        },
    });
}

export function useUpdateWebsitePageDecisionMutation(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        onMutate: () => ({ workspaceId }),
        mutationFn: (args: UpdatePageDecisionArgs) => updateWebsitePageDecision(args),
        onSuccess: (_data, _args, context) => {
            const workspaceId = context.workspaceId;
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.runs(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.pagesAll(workspaceId),
            });
        },
    });
}

export function useBulkUpdateWebsitePageDecisionMutation(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        onMutate: () => ({ workspaceId }),
        mutationFn: (args: BulkUpdatePageDecisionArgs) => bulkUpdateWebsitePageDecision(args),
        onSuccess: (_data, _args, context) => {
            const workspaceId = context.workspaceId;
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.runs(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.pagesAll(workspaceId),
            });
        },
    });
}

export function useBulkUpdateWebsiteDocumentDecisionMutation(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (args: BulkUpdateDocumentDecisionArgs) =>
            bulkUpdateWebsiteDocumentDecision(args),
        onMutate: async (
            args
        ): Promise<{ previousPages: WebsiteSyncPagesSnapshot; workspaceId: string }> => {
            const pagesQueryKey = queryKeys.websiteSync.pagesAll(workspaceId);
            await queryClient.cancelQueries({ queryKey: pagesQueryKey });
            const previousPages =
                queryClient.getQueriesData<WebsiteSyncPagesResult>({
                    queryKey: pagesQueryKey,
                }) ?? [];

            for (const [queryKey, current] of previousPages) {
                const filters = getFiltersFromPagesQueryKey(queryKey);
                queryClient.setQueryData(
                    queryKey,
                    getOptimisticPagesResult(current, args, filters)
                );
            }

            return { previousPages, workspaceId };
        },
        onError: (_error, _args, context) => {
            for (const [queryKey, data] of context?.previousPages ?? []) {
                queryClient.setQueryData(queryKey, data);
            }
        },
        onSettled: (_data, _error, _args, context) => {
            if (!context) return;
            const workspaceId = context.workspaceId;
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.runs(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.pagesAll(workspaceId),
            });
        },
    });
}

export function useApplyWebsiteDocumentPolicyChangesMutation(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        onMutate: () => ({ workspaceId }),
        mutationFn: (args: ApplyWebsiteDocumentPolicyChangesArgs) =>
            applyWebsiteDocumentPolicyChanges(args),
        onSettled: (_data, _error, _args, context) => {
            if (!context) return;
            const workspaceId = context.workspaceId;
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.runs(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.pagesAll(workspaceId),
            });
        },
    });
}
